// features/super-admin/pages/AuditLogDetailPage.tsx
/**
 * SUPER ADMIN — AUDIT LOG DETAIL
 * Comprehensive view of a single audit log entry with before/after comparison.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Activity, Building2, User, Clock,
    Globe, Monitor, FileText, Key, Shield, Tag,
    Copy, ExternalLink, ChevronRight, Calendar,
    AlertTriangle, CheckCircle2, Info, RefreshCw,
    ArrowRightLeft, Layers, ShoppingCart, DollarSign,
    Package, Truck, Users, CreditCard, Star,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface AuditLogDetail {
    id: string;
    tenantId: string;
    tenantName: string;
    tenantSlug: string;
    userId: string;
    userName: string;
    userEmail: string;
    userAvatar: string | null;
    action: string;
    entityType: string;
    entityId: string | null;
    entityName: string | null;
    oldValues: Record<string, unknown> | null;
    newValues: Record<string, unknown> | null;
    metadata: Record<string, unknown> | null;
    ipAddress: string | null;
    userAgent: string | null;
    location: string | null;
    createdAt: string;
    // Related logs
    relatedLogs: Array<{
        id: string;
        action: string;
        createdAt: string;
    }>;
}

// ============================================================
// CONSTANTS
// ============================================================

const ACTION_ICONS: Record<string, React.ElementType> = {
    USER_LOGIN: Key,
    USER_LOGOUT: Key,
    PASSWORD_CHANGED: Shield,
    TENANT_REGISTERED: Building2,
    TENANT_UPDATED: Building2,
    TENANT_DEACTIVATED: Building2,
    TENANT_ACTIVATED: Building2,
    STAFF_CREATED: Users,
    STAFF_UPDATED: Users,
    STAFF_DEACTIVATED: Users,
    BUSINESS_CREATED: Building2,
    BUSINESS_UPDATED: Building2,
    PRODUCT_CREATED: Package,
    PRODUCT_UPDATED: Package,
    PRODUCT_DELETED: Package,
    PRODUCT_BULK_UPLOADED: Package,
    INVENTORY_COUNT_STARTED: Layers,
    INVENTORY_COUNT_COMPLETED: Layers,
    INVENTORY_TRANSFER_CREATED: ArrowRightLeft,
    STOCK_ADJUSTED: RefreshCw,
    STOCK_CONFIRMED: CheckCircle2,
    SALE_COMPLETED: ShoppingCart,
    SALE_VOIDED: ShoppingCart,
    SALE_REFUNDED: ShoppingCart,
    BANK_ACCOUNT_CREATED: CreditCard,
    EXPENSE_CREATED: DollarSign,
    EXPENSE_APPROVED: DollarSign,
    INVOICE_CREATED: FileText,
    VENDOR_CREATED: Truck,
    VENDOR_UPDATED: Truck,
    REQUISITION_CREATED: FileText,
    PO_CREATED: FileText,
    GRN_CREATED: Package,
    GRN_CONFIRMED: CheckCircle2,
    PURCHASE_RETURN_CREATED: ArrowRightLeft,
};

const ACTION_COLORS: Record<string, string> = {
    created: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    updated: 'text-blue-600 bg-blue-50 border-blue-200',
    deleted: 'text-rose-600 bg-rose-50 border-rose-200',
    deactivated: 'text-rose-600 bg-rose-50 border-rose-200',
    activated: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    completed: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    started: 'text-amber-600 bg-amber-50 border-amber-200',
    approved: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    rejected: 'text-rose-600 bg-rose-50 border-rose-200',
    confirmed: 'text-emerald-600 bg-emerald-50 border-emerald-200',
    voided: 'text-rose-600 bg-rose-50 border-rose-200',
    refunded: 'text-amber-600 bg-amber-50 border-amber-200',
    login: 'text-blue-600 bg-blue-50 border-blue-200',
    logout: 'text-slate-600 bg-slate-50 border-slate-200',
};

// ============================================================
// FORMATTERS
// ============================================================

const formatDateTime = (date: string) => {
    try { return new Date(date).toLocaleString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
    catch { return 'N/A'; }
};

const formatDate = (date: string) => {
    try { return new Date(date).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return 'N/A'; }
};

// ============================================================
// HELPERS
// ============================================================

function getActionColor(action: string): string {
    const lower = action.toLowerCase();
    for (const [key, color] of Object.entries(ACTION_COLORS)) {
        if (lower.includes(key)) return color;
    }
    return 'text-slate-600 bg-slate-50 border-slate-200';
}

function getActionIcon(action: string): React.ElementType {
    return ACTION_ICONS[action] || Activity;
}

function getInitials(name: string): string {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function formatValue(value: unknown): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
}

// ============================================================
// DIFF VIEWER
// ============================================================

function DiffViewer({ oldValues, newValues }: { oldValues: Record<string, unknown> | null; newValues: Record<string, unknown> | null }) {
    if (!oldValues && !newValues) {
        return (
            <div className="text-center py-8">
                <Info size={32} className="text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-400">No change data available for this log entry.</p>
            </div>
        );
    }

    // Get all unique keys from both old and new values
    const allKeys = new Set<string>();
    if (oldValues) Object.keys(oldValues).forEach(k => allKeys.add(k));
    if (newValues) Object.keys(newValues).forEach(k => allKeys.add(k));

    const keys = Array.from(allKeys);

    return (
        <div className="space-y-2">
            {keys.map(key => {
                const oldVal = oldValues?.[key];
                const newVal = newValues?.[key];
                const hasChanged = JSON.stringify(oldVal) !== JSON.stringify(newVal);

                return (
                    <div key={key} className={clsx(
                        'grid grid-cols-3 gap-3 p-3 rounded-xl border transition-all',
                        hasChanged ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'
                    )}>
                        <div className="flex items-center gap-2">
                            {hasChanged ? (
                                <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                            ) : (
                                <CheckCircle2 size={14} className="text-slate-300 flex-shrink-0" />
                            )}
                            <span className="text-xs font-bold text-slate-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        </div>
                        <div className="text-xs font-mono text-slate-500 bg-white rounded-lg p-2 break-all">
                            {formatValue(oldVal)}
                        </div>
                        <div className={clsx(
                            'text-xs font-mono rounded-lg p-2 break-all',
                            hasChanged ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'bg-white text-slate-500'
                        )}>
                            {formatValue(newVal)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function AuditLogDetailPage() {
    const { logId } = useParams<{ logId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore(s => s.user);

    const { data: log, isLoading, isError, refetch } = useQuery<AuditLogDetail>({
        queryKey: ['super-admin-audit-log', logId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/super-admin/audit-logs/${logId}`);
            return data?.data;
        },
        enabled: !!logId && !!user,
    });

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 rounded-2xl border-4 border-slate-200 border-t-slate-600" />
        </div>
    );

    if (isError || !log) return (
        <div className="min-h-screen flex items-center justify-center">
            <ErrorState title="Audit log not found" message="This log entry doesn't exist or has been removed." onBack={() => navigate('/super-admin/audit-logs')} />
        </div>
    );

    const ActionIcon = getActionIcon(log.action);
    const actionColor = getActionColor(log.action);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <div className="max-w-[1400px] mx-auto p-4 lg:p-6 space-y-5">

                {/* Back */}
                <button onClick={() => navigate('/super-admin/audit-logs')}
                    className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 group">
                    <ArrowLeft size={16} className="group-hover:-translate-x-1" /> Back to Audit Logs
                </button>

                {/* ========== HERO CARD ========== */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden bg-white rounded-3xl shadow-xl border border-slate-200">
                    <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-slate-600 via-slate-700 to-slate-800" />
                    <div className="p-8 lg:p-10">
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                            <div className="flex items-center gap-5">
                                <div className={clsx('w-16 h-16 rounded-2xl flex items-center justify-center shadow-xl', actionColor)}>
                                    <ActionIcon size={28} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h1 className="text-2xl lg:text-3xl font-black text-slate-900">
                                            {log.action.replace(/_/g, ' ')}
                                        </h1>
                                        <Badge variant="info" size="md">
                                            {log.entityType?.replace(/_/g, ' ')}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                                        <span className="flex items-center gap-1.5">
                                            <Calendar size={14} /> {formatDateTime(log.createdAt)}
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <Building2 size={14} /> {log.tenantName}
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <User size={14} /> {log.userName || log.userEmail || 'System'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => refetch()}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium shadow-sm">
                                <RefreshCw size={14} /> Refresh
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* ========== DETAIL GRID ========== */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* LEFT: Key Info */}
                    <div className="lg:col-span-2 space-y-5">
                        {/* Change Diff */}
                        <Card>
                            <CardHeader gradient>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <ArrowRightLeft size={18} className="text-amber-500" /> Changes
                                </h3>
                            </CardHeader>
                            <CardContent>
                                <DiffViewer oldValues={log.oldValues} newValues={log.newValues} />
                            </CardContent>
                        </Card>

                        {/* Related Logs */}
                        {log.relatedLogs && log.relatedLogs.length > 0 && (
                            <Card>
                                <CardHeader gradient>
                                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                        <Activity size={18} className="text-blue-500" /> Related Activity
                                    </h3>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {log.relatedLogs.map((related) => (
                                            <button
                                                key={related.id}
                                                onClick={() => navigate(`/super-admin/audit-logs/${related.id}`)}
                                                className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-blue-50 transition-colors group"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Activity size={14} className="text-slate-400 group-hover:text-blue-500" />
                                                    <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600">
                                                        {related.action.replace(/_/g, ' ')}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs text-slate-400">{formatDate(related.createdAt)}</span>
                                                    <ChevronRight size={14} className="text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* RIGHT: Metadata */}
                    <div className="space-y-5">
                        {/* Tenant Info */}
                        <Card>
                            <CardHeader gradient>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <Building2 size={18} className="text-violet-500" /> Tenant
                                </h3>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                                        {getInitials(log.tenantName)}
                                    </div>
                                    <div>
                                        <p className="font-bold text-sm text-slate-800">{log.tenantName}</p>
                                        <code className="text-xs text-slate-400 font-mono">{log.tenantSlug || log.tenantId}</code>
                                    </div>
                                </div>
                                <button
                                    onClick={() => navigate(`/super-admin/tenants/${log.tenantId}`)}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded-xl hover:bg-violet-100 transition-colors">
                                    <ExternalLink size={14} /> View Tenant
                                </button>
                            </CardContent>
                        </Card>

                        {/* User Info */}
                        <Card>
                            <CardHeader gradient>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <User size={18} className="text-blue-500" /> Actor
                                </h3>
                            </CardHeader>
                            <CardContent>
                                {log.userName ? (
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                                            {getInitials(log.userName)}
                                        </div>
                                        <div>
                                            <p className="font-bold text-sm text-slate-800">{log.userName}</p>
                                            {log.userEmail && <p className="text-xs text-slate-400">{log.userEmail}</p>}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                                            <User size={18} className="text-slate-400" />
                                        </div>
                                        <p className="text-sm text-slate-500">System / Automated</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Technical Details */}
                        <Card>
                            <CardHeader gradient>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                    <Monitor size={18} className="text-slate-500" /> Technical Details
                                </h3>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center py-1.5">
                                        <span className="text-xs text-slate-400 flex items-center gap-1.5">
                                            <Globe size={12} /> IP Address
                                        </span>
                                        <span className="text-xs font-mono font-medium text-slate-700">
                                            {log.ipAddress || 'N/A'}
                                        </span>
                                    </div>
                                    {log.location && (
                                        <div className="flex justify-between items-center py-1.5">
                                            <span className="text-xs text-slate-400 flex items-center gap-1.5">
                                                <Globe size={12} /> Location
                                            </span>
                                            <span className="text-xs font-medium text-slate-700">{log.location}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-start py-1.5">
                                        <span className="text-xs text-slate-400 flex items-center gap-1.5 flex-shrink-0">
                                            <Monitor size={12} /> User Agent
                                        </span>
                                        <span className="text-xs text-slate-500 text-right max-w-[200px] truncate">
                                            {log.userAgent || 'N/A'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center py-1.5">
                                        <span className="text-xs text-slate-400 flex items-center gap-1.5">
                                            <Tag size={12} /> Log ID
                                        </span>
                                        <code className="text-[10px] font-mono text-slate-400">{log.id}</code>
                                    </div>
                                    {log.entityId && (
                                        <div className="flex justify-between items-center py-1.5">
                                            <span className="text-xs text-slate-400 flex items-center gap-1.5">
                                                <FileText size={12} /> Entity ID
                                            </span>
                                            <code className="text-[10px] font-mono text-slate-400">{log.entityId.slice(0, 12)}...</code>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Raw JSON (for debugging) */}
                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                            <Card>
                                <CardHeader gradient>
                                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                        <FileText size={18} className="text-amber-500" /> Metadata
                                    </h3>
                                </CardHeader>
                                <CardContent>
                                    <pre className="text-xs text-slate-600 font-mono whitespace-pre-wrap bg-slate-50 rounded-xl p-3 max-h-48 overflow-y-auto">
                                        {JSON.stringify(log.metadata, null, 2)}
                                    </pre>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}